import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { usePageTitle } from '../contexts/PageTitleContext';
import {
  ClockIcon,
  UserIcon,
  CalendarIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';

interface ActivityLog {
  id: string;
  user_id: string;
  user_email: string;
  action: string;
  resource_type: string;
  resource_id: string;
  resource_name: string;
  changes: Array<{
    field: string;
    old_value: any;
    new_value: any;
  }>;
  details: Record<string, any>;
  ip_address: string;
  user_agent: string;
  status: string;
  error_message: string;
  timestamp: string;
  company_id: string;
}

const AssetActivity: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { setPageTitle } = usePageTitle();
  const [limit] = useState(100);
  const [offset] = useState(0);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  // Fetch activity logs for this asset
  const { data: activityData, isLoading, error } = useQuery({
    queryKey: ['activity-logs', id, limit, offset],
    queryFn: () => apiService.getActivityLogs('file', id!, limit, offset),
    enabled: !!id,
  });

  // Fetch file details for the page title
  const { data: fileData } = useQuery({
    queryKey: ['asset', id],
    queryFn: () => apiService.getFileById(id!),
    enabled: !!id,
  });

  useEffect(() => {
    if (fileData?.file) {
      setPageTitle('Activity History', `View activity logs for ${fileData.file.file_name}`);
    } else {
      setPageTitle('Activity History', 'View activity logs for this asset');
    }
  }, [fileData, setPageTitle]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  const formatAction = (action: string) => {
    // Convert action names to readable format
    return action
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  };

  const formatFieldName = (field: string) => {
    return field
      .split('.')
      .map(part => part.replace(/_/g, ' '))
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' → ');
  };

  const formatValue = (value: any): string => {
    if (value === null || value === undefined) {
      return 'None';
    }
    if (typeof value === 'boolean') {
      return value ? 'Enabled' : 'Disabled';
    }
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }
    return String(value);
  };

  const toggleItem = (itemId: string) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const expandAll = () => {
    const allIds = new Set<string>(logs.map((log: ActivityLog) => log.id));
    setExpandedItems(allIds);
  };

  const collapseAll = () => {
    setExpandedItems(new Set());
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-sm text-gray-500">Loading activity history...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <XCircleIcon className="mx-auto h-12 w-12 text-red-500" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Error loading activity logs</h3>
        <p className="mt-1 text-sm text-gray-500">Please try again later.</p>
        <button
          onClick={() => navigate('/assets')}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Back to Assets
        </button>
      </div>
    );
  }

  const logs = activityData?.logs || [];

  return (
    <div className="space-y-6">
      {/* Header with back button */}
    
      {/* Activity logs */}
      <div className="card">
        {logs.length === 0 ? (
          <div className="text-center py-12">
            <ClockIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No activity yet</h3>
            <p className="mt-1 text-sm text-gray-500">
              Activity logs for this asset will appear here.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">
                Recent Activity ({logs.length})
              </h2>
              <div className="flex space-x-2">
                <button
                  onClick={expandAll}
                  className="px-3 py-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                >
                  Expand All
                </button>
                <button
                  onClick={collapseAll}
                  className="px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Collapse All
                </button>
              </div>
            </div>

            <div className="space-y-2">
              {logs.map((log: ActivityLog) => {
                const isExpanded = expandedItems.has(log.id);
                
                return (
                  <div
                    key={log.id}
                    className="border border-gray-200 rounded-lg overflow-hidden transition-all"
                  >
                    {/* Accordion Header - Always visible */}
                    <button
                      onClick={() => toggleItem(log.id)}
                      className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center space-x-3 flex-1">
                        <div className={`p-2 rounded-lg ${
                          log.status === 'success' 
                            ? 'bg-green-100 text-green-600' 
                            : log.status === 'failure'
                            ? 'bg-red-100 text-red-600'
                            : 'bg-blue-100 text-blue-600'
                        }`}>
                          {log.status === 'success' ? (
                            <CheckCircleIcon className="w-4 h-4" />
                          ) : log.status === 'failure' ? (
                            <XCircleIcon className="w-4 h-4" />
                          ) : (
                            <ClockIcon className="w-4 h-4" />
                          )}
                        </div>
                        <div className="flex-1 text-left">
                          <h3 className="text-sm font-semibold text-gray-900">
                            {formatAction(log.action)}
                          </h3>
                          <div className="flex items-center space-x-4 mt-1">
                            <div className="flex items-center space-x-1 text-xs text-gray-500">
                              <UserIcon className="w-3 h-3" />
                              <span>{log.user_email}</span>
                            </div>
                            <div className="flex items-center space-x-1 text-xs text-gray-500">
                              <CalendarIcon className="w-3 h-3" />
                              <span>{formatDate(log.timestamp)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-3">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          log.status === 'success'
                            ? 'bg-green-100 text-green-800'
                            : log.status === 'failure'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {log.status}
                        </span>
                        {isExpanded ? (
                          <ChevronUpIcon className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronDownIcon className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                    </button>

                    {/* Accordion Content - Only visible when expanded */}
                    {isExpanded && (
                      <div className="px-4 pb-4 border-t border-gray-100 bg-gray-50">
                        {/* Changes */}
                        {log.changes && log.changes.length > 0 && (
                          <div className="mt-3">
                            <h4 className="text-xs font-medium text-gray-700 mb-2">Changes:</h4>
                            <div className="space-y-2">
                              {log.changes.map((change, idx) => (
                                <div key={idx} className="text-xs bg-white rounded p-3 border border-gray-200">
                                  <div className="font-medium text-gray-700 mb-2">
                                    {formatFieldName(change.field)}
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <span className="text-red-600 line-through">
                                      {formatValue(change.old_value)}
                                    </span>
                                    <span className="text-gray-400">→</span>
                                    <span className="text-green-600 font-medium">
                                      {formatValue(change.new_value)}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Error message */}
                        {log.error_message && (
                          <div className="mt-3">
                            <div className="text-xs text-red-600 bg-red-50 rounded p-3 border border-red-200">
                              <span className="font-medium">Error:</span> {log.error_message}
                            </div>
                          </div>
                        )}

                        {/* Additional details */}
                        {log.ip_address && (
                          <div className="mt-3 text-xs text-gray-500">
                            <span className="font-medium">IP Address:</span> {log.ip_address}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssetActivity;

